// Shared sync utilities for Routes, RouteStops, and Shipments
// Provides reusable date comparison and upsert logic for entity synchronization

import dayjs from 'dayjs';

// TYPES & INTERFACES

// Entity interface that sync utilities work with
// All entities must have id, syncedOn, updatedDate, createdDate, and syncStatus fields
export interface SyncableEntity {
  id: number;
  syncedOn?: Date | null;
  updatedDate?: Date | null;
  createdDate?: Date | null;
  syncStatus?: string;
}

// Configuration for upsert operation
export interface UpsertConfig<T extends SyncableEntity> {
  // Function to get entity ID for comparison
  getId: (entity: T) => number;
  // Function to mark entity as pending sync (default is to set syncStatus to 'pending')
  markAsPending?: (entity: T) => T;
}

// Result of upsert operation
export interface UpsertResult {
  inserted: number;
  updated: number;
  deleted: number;
  keptLocal: number;
}

// PUBLIC FUNCTIONS ===========================================================

// Generic upsert function that compares incoming API data with cached data
// - Deletes local items not present in API response
// - Inserts routes that are new (not present in cache)
// - Updates existing routes only if API data is newer
// - Keeps local routes that are newer and marks them as pending sync
export const upsertEntities = <T extends SyncableEntity>(
  apiEntities: T[],
  cachedEntities: T[],
  config: UpsertConfig<T>
): { toInsert: T[]; toUpdate: T[]; toDelete: number[]; toKeepLocal: T[] } => {
  const toInsert: T[] = [];
  const toUpdate: T[] = [];
  const toDelete: number[] = [];
  const toKeepLocal: T[] = [];

  const cachedEntitiesMap = createEntityMap(cachedEntities, config);
  // Process API entities: insert new ones or update existing ones
  for (const apiEntity of apiEntities) {
    const id = config.getId(apiEntity);
    const cachedEntity = cachedEntitiesMap.get(id);

    if (cachedEntity) {
      const result = processExistingEntity(cachedEntity, apiEntity, config);
      if (result.toUpdate) {
        toUpdate.push(result.toUpdate);
      }
      if (result.toKeepLocal) {
        toKeepLocal.push(result.toKeepLocal);
      }
    } else {
      toInsert.push(apiEntity);
    }
  }

  const apiEntitiesMap = createEntityMap(apiEntities, config);
  // Find cached entities not in API response - these should be deleted
  for (const cachedEntity of cachedEntities) {
    const id = config.getId(cachedEntity);
    if (!apiEntitiesMap.has(id)) {
      toDelete.push(id);
    }
  }

  return { toInsert, toUpdate, toDelete, toKeepLocal };
};

// PRIVATE FUNCTIONS ===========================================================

// Gets the effective date for comparison based on SyncedOn, UpdatedDate, CreatedDate priority
const getEffectiveDate = (entity: SyncableEntity): Date => {
  const dates: Date[] = [entity.syncedOn, entity.updatedDate, entity.createdDate].filter(
    Boolean
  ) as Date[];
  const sortedDates = dates.toSorted((a, b) => dayjs(b).diff(dayjs(a)));
  return sortedDates[0] as Date;
};

// Compares two entities by their effective dates
// Returns -1 if entity1 is newer, 1 if entity2 is newer, 0 if equal or both null
const compareByDate = (entity1: SyncableEntity, entity2: SyncableEntity): number => {
  const d1 = dayjs(getEffectiveDate(entity1));
  const d2 = dayjs(getEffectiveDate(entity2));
  const d1Valid = d1.isValid();
  const d2Valid = d2.isValid();

  // If both dates are invalid/null, consider them equal
  if (!d1Valid && !d2Valid) return 0;

  // If only one has a valid date, it's considered newer
  if (!d1Valid) return 1; // entity2 is newer
  if (!d2Valid) return -1; // entity1 is newer

  if (d1.isAfter(d2)) return -1; // entity1 is newer
  if (d1.isBefore(d2)) return 1; // entity2 is newer

  return 0; // dates are equal
};

// Determines if local cache should be updated based on date comparison
// If local has newer data, keep local and mark as pending sync
// If API has newer data, overwrite local
const shouldUpdateLocal = (
  localEntity: SyncableEntity,
  apiEntity: SyncableEntity
): { shouldUpdate: boolean; shouldKeepLocal: boolean } => {
  const comparison = compareByDate(localEntity, apiEntity);

  // If local is newer (comparison < 0), keep local and mark as pending
  if (comparison < 0) {
    return { shouldUpdate: false, shouldKeepLocal: true };
  }

  // If API is newer (comparison > 0) or equal, update local
  return { shouldUpdate: true, shouldKeepLocal: false };
};

// Creates a map of entity ID to entity for efficient lookup
const createEntityMap = <T extends SyncableEntity>(
  entities: T[],
  config: UpsertConfig<T>
): Map<number, T> => {
  const entityMap = new Map<number, T>();
  for (const entity of entities) {
    entityMap.set(config.getId(entity), entity);
  }
  return entityMap;
};

// Processes an existing entity to determine if it should be updated or kept locally
const processExistingEntity = <T extends SyncableEntity>(
  cachedEntity: T,
  apiEntity: T,
  config: UpsertConfig<T>
): { toUpdate?: T; toKeepLocal?: T } => {
  const { shouldUpdate, shouldKeepLocal } = shouldUpdateLocal(cachedEntity, apiEntity);

  if (shouldKeepLocal) {
    // Local entity is newer than API - keep local data but mark as pending sync
    // This ensures local changes are preserved and will be synced to server later
    // If markAsPending is provided, update syncStatus to 'pending' to track sync state
    if (config.markAsPending) {
      const markedEntity = config.markAsPending(cachedEntity);
      return { toKeepLocal: markedEntity };
    }
    // Keep local as-is without marking as pending (if markAsPending not provided)
    return { toKeepLocal: cachedEntity };
  }

  if (shouldUpdate) {
    // Update with API data
    return { toUpdate: apiEntity };
  }

  // If dates are equal and both exist, skip update (no change needed)
  return {};
};
